// src/components/sliders/EditSliderModal.js
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FaTimes, FaExchangeAlt } from "react-icons/fa";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import {
  validateSliderFile,
  optimizeSliderImage,
  formatBytes,
  ALLOWED_EXTENSIONS
} from "../../utils/imageOptimizer";

const EditSliderModal = ({ slider, show, onHide, onSuccess }) => {
  const [sortOrder, setSortOrder] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);

  // Sync state with selected slider
  useEffect(() => {
    if (slider) {
      setSortOrder(slider.sortOrder !== undefined ? slider.sortOrder : 1);
      setIsActive(slider.isActive !== undefined ? slider.isActive : true);
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMsg("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [slider, show]);

  // Clean up object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (file) => {
    setErrorMsg("");
    if (!file) return;

    try {
      validateSliderFile(file);
      setSelectedFile(file);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const newUrl = URL.createObjectURL(file);
      setPreviewUrl(newUrl);
    } catch (err) {
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setErrorMsg(err.message);
    }
  };

  const cancelReplacement = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setErrorMsg("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!slider?.id) return;

    const orderNum = parseInt(sortOrder, 10);
    if (isNaN(orderNum) || orderNum < 0) {
      setErrorMsg("Sort order must be a non-negative number.");
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");

      const docRef = doc(db, "sliders", slider.id);

      // Scenario A: Image was replaced
      if (selectedFile) {
        // 1. Optimize image client-side (max 1920px width, aspect-ratio preserved, WebP preferred)
        const { blob, contentType, extension } = await optimizeSliderImage(selectedFile);

        // 2. Upload new image using a timestamped path to prevent CDN caching issues
        const newStoragePath = `sliders/${slider.id}/banner_${Date.now()}.${extension}`;
        const newStorageRef = ref(storage, newStoragePath);

        await uploadBytes(newStorageRef, blob, {
          contentType,
          customMetadata: {
            originalName: selectedFile.name,
            replacedAt: new Date().toISOString(),
          }
        });

        // 3. Obtain new download URL
        const newImageUrl = await getDownloadURL(newStorageRef);

        // 4. Update Firestore with new imageUrl and storagePath
        await updateDoc(docRef, {
          imageUrl: newImageUrl,
          storagePath: newStoragePath,
          sortOrder: orderNum,
          isActive: Boolean(isActive),
          updatedAt: serverTimestamp(),
        });

        // 5. Delete old storage object to prevent orphans
        if (slider.storagePath && slider.storagePath !== newStoragePath) {
          try {
            const oldStorageRef = ref(storage, slider.storagePath);
            await deleteObject(oldStorageRef);
          } catch (delErr) {
            console.warn("Could not delete old storage object (it may have been already deleted):", delErr);
          }
        }
      } else {
        // Scenario B: Only metadata (sort order / active status) changed
        await updateDoc(docRef, {
          sortOrder: orderNum,
          isActive: Boolean(isActive),
          updatedAt: serverTimestamp(),
        });
      }

      cancelReplacement();
      if (onSuccess) {
        onSuccess("Slider updated successfully!");
      }
      onHide();
    } catch (err) {
      console.error("Error updating slider:", err);
      setErrorMsg(err.message || "Failed to update slider. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!saving) {
          cancelReplacement();
          onHide();
        }
      }}
      centered
      backdrop={saving ? "static" : true}
      size="lg"
    >
      <Modal.Header closeButton={!saving} className="border-0 pb-0">
        <Modal.Title className="text-h2" style={{ color: "var(--accent-lime)" }}>
          Edit Slider
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSave}>
        <Modal.Body className="pt-3">
          {errorMsg && (
            <div className="alert alert-danger py-2 px-3 rounded-3 small mb-3" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Banner Preview & Replacement Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label className="fw-semibold theme-form-label mb-0">
                Banner Image
              </Form.Label>
              {!selectedFile ? (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-decoration-none"
                  style={{ color: "var(--accent-lime)", fontSize: 13, fontWeight: 600 }}
                  onClick={() => !saving && fileInputRef.current?.click()}
                  disabled={saving}
                >
                  <FaExchangeAlt className="me-1" /> Replace Image
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-decoration-none text-danger"
                  style={{ fontSize: 13, fontWeight: 600 }}
                  onClick={cancelReplacement}
                  disabled={saving}
                >
                  <FaTimes className="me-1" /> Cancel Replacement
                </button>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
              onChange={(e) => handleFileChange(e.target.files?.[0])}
              disabled={saving}
            />

            <div
              style={{
                background: "var(--bg-primary)",
                border: selectedFile ? "2px dashed var(--accent-lime)" : "1px solid var(--divider)",
                borderRadius: 12,
                padding: 14,
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxHeight: 240,
                  borderRadius: 8,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#000",
                }}
              >
                <img
                  src={previewUrl || slider?.imageUrl}
                  alt="Slider Banner"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 240,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>

              <div className="d-flex align-items-center justify-content-between mt-2 px-1">
                <span className="small text-truncate" style={{ color: "var(--text-primary)" }}>
                  {selectedFile ? (
                    <>
                      <strong className="text-warning">Replacement:</strong> {selectedFile.name}
                    </>
                  ) : (
                    <span style={{ color: "var(--text-secondary)" }}>Current Banner</span>
                  )}
                </span>

                {selectedFile ? (
                  <span className="badge" style={{ background: "rgba(217, 255, 31, 0.15)", color: "var(--accent-lime)" }}>
                    {formatBytes(selectedFile.size)}
                  </span>
                ) : (
                  <span className="badge" style={{ background: "rgba(255, 255, 255, 0.08)", color: "var(--text-secondary)" }}>
                    In Storage
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="row g-3">
            {/* Sort Order */}
            <div className="col-md-6">
              <Form.Group controlId="editSortOrder">
                <Form.Label className="fw-semibold theme-form-label">Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  disabled={saving}
                  required
                  style={{ borderRadius: 8, padding: "9px 14px" }}
                />
                <Form.Text className="small" style={{ color: "var(--text-secondary)" }}>
                  Lower numbers appear first on the homepage.
                </Form.Text>
              </Form.Group>
            </div>

            {/* Active / Inactive Status Switch */}
            <div className="col-md-6">
              <Form.Group controlId="editIsActive">
                <Form.Label className="fw-semibold theme-form-label d-block">Status</Form.Label>
                <div className="d-flex align-items-center gap-2 pt-2">
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="editActiveSwitch"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={saving}
                      style={{ width: "2.6em", height: "1.3em", cursor: "pointer" }}
                    />
                  </div>
                  <label
                    htmlFor="editActiveSwitch"
                    className={`fw-semibold small mb-0 ${isActive ? "text-success" : "text-danger"}`}
                    style={{ cursor: "pointer" }}
                  >
                    {isActive ? "Active (Visible on homepage)" : "Inactive (Hidden)"}
                  </label>
                </div>
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="secondary"
            onClick={() => {
              cancelReplacement();
              onHide();
            }}
            disabled={saving}
            style={{ borderRadius: 8, padding: "8px 20px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={saving}
            style={{ borderRadius: 8, padding: "8px 24px", minWidth: 120 }}
          >
            {saving ? (
              <>
                <span className="admin-loader-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditSliderModal;
